import { atom, useRecoilState, useResetRecoilState, useSetRecoilState } from "recoil";
import { useCallback } from "react";
import { randomString } from "../../Functions";
import { FeedsState, PostType, UseFeedsReturn } from "./type";
import * as socialAPI from "../../Data/Social";
import { validatePost, postsState } from "./Posts";

// Feeds state atom
export const feedsState = atom<FeedsState>({
    key: "feedsState" + randomString(4),
    default: {
        homeFeed: [],
        homeFeedGroups: [],
        discoverFeed: [],
        trendingFeed: [],
        hashtagFeeds: {},
        userFeeds: {},
        searchResults: [],
        loading: false,
        error: null
    },
    effects: [ ({ setSelf}) => {
        (async()=>{
            try {
                await socialAPI.socialDatabase.init();
                
                // Load recent cached posts into homeFeed on startup
                const recentPosts = await socialAPI.socialDatabase.getAllPosts();
                const recentPostIds = recentPosts
                    .sort((a, b) => (b.created_at ?? 0) - (a.created_at ?? 0))
                    .slice(0, 50) // Limit to recent 50 posts
                    .map(post => post.postid);
                
                if (recentPostIds.length > 0) {
                    setSelf({
                        homeFeed: recentPostIds,
                        homeFeedGroups: [],
                        discoverFeed: [],
                        trendingFeed: [],
                        hashtagFeeds: {},
                        userFeeds: {},
                        searchResults: [],
                        loading: false,
                        error: null
                    });
                }
                
            } catch (error) {
                console.error('Failed to initialize feeds state:', error);
                setSelf({
                    homeFeed: [],
                    homeFeedGroups: [],
                    discoverFeed: [],
                    trendingFeed: [],
                    hashtagFeeds: {},
                    userFeeds: {},
                    searchResults: [],
                    loading: false,
                    error: error instanceof Error ? error.message : "Failed to initialize feeds state"
                });
            }
        })();
    } ]
});

export const useFeeds = (): UseFeedsReturn => {
    const [state, setState] = useRecoilState(feedsState);
    const resetFeeds = useResetRecoilState(feedsState);
    const setPosts = useSetRecoilState(postsState);

    // Get home feed
    const getHomeFeed = useCallback(async (options: { limit?: number; offset?: number; append?: boolean; grouped?: boolean } = {}): Promise<PostType[]> => {
        const { limit = 20, offset = 0, append = false, grouped = true } = options;
        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const result = await socialAPI.getFeed({type: 'home', limit, offset, grouped});

            if (result && Array.isArray(result.posts)) {
                const posts = result.posts;
                const validPosts = posts.filter(validatePost);
                const postIds = validPosts.map(post => post.postid);

                await socialAPI.socialDatabase.savePosts(validPosts);

                // Update postsState with fetched posts
                setPosts(prev => ({
                    ...prev,
                    items: { ...prev.items, ...Object.fromEntries(validPosts.map(post => [post.postid, post])) }
                }));

                setState(prev => ({
                    ...prev,
                    homeFeed: append ? [...prev.homeFeed, ...postIds] : postIds,
                    homeFeedGroups: (result as any).groups || [],
                    loading: false
                }));

                return validPosts;
            } else {
                setState(prev => ({ ...prev, loading: false }));
                return [];
            }
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: error instanceof Error ? error.message : "Failed to get home feed",
                loading: false
            }));
            console.error('Failed to get home feed:', error);
            return [];
        }
    }, [setState, setPosts]);

    // Get discover feed
    const getDiscoverFeed = useCallback(async (): Promise<PostType[]> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const result = await socialAPI.getFeed({type: 'discover'});
            
            if (result && Array.isArray(result.posts)) {
                const posts = result.posts;
                const validPosts = posts.filter(validatePost);
                const postIds = validPosts.map(post => post.postid);

                await socialAPI.socialDatabase.savePosts(validPosts);
                
                // Update postsState with fetched posts
                setPosts(prev => ({
                    ...prev,
                    items: { ...prev.items, ...Object.fromEntries(validPosts.map(post => [post.postid, post])) }
                }));
                
                setState(prev => ({
                    ...prev,
                    discoverFeed: postIds,
                    loading: false
                }));
                
                return validPosts;
            } else {
                setState(prev => ({ ...prev, loading: false }));
                return [];
            }
        } catch (error) {
            setState(prev => ({ 
                ...prev, 
                error: error instanceof Error ? error.message : "Failed to get discover feed",
                loading: false
            }));
            console.error('Failed to get discover feed:', error);
            return [];
        }
    }, [setState, setPosts]);

    // Get trending feed
    const getTrendingFeed = useCallback(async (): Promise<PostType[]> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const result = await socialAPI.getFeed({type: 'trending'});
            
            if (result && Array.isArray(result.posts)) {
                const posts = result.posts;
                const validPosts = posts.filter(validatePost);
                const postIds = validPosts.map(post => post.postid);

                await socialAPI.socialDatabase.savePosts(validPosts);
                
                // Update postsState with fetched posts
                setPosts(prev => ({
                    ...prev,
                    items: { ...prev.items, ...Object.fromEntries(validPosts.map(post => [post.postid, post])) }
                }));
                
                setState(prev => ({
                    ...prev,
                    trendingFeed: postIds,
                    loading: false
                }));
                
                return validPosts;
            } else {
                setState(prev => ({ ...prev, loading: false }));
                return [];
            }
        } catch (error) {
            setState(prev => ({ 
                ...prev, 
                error: error instanceof Error ? error.message : "Failed to get trending feed",
                loading: false
            }));
            console.error('Failed to get trending feed:', error);
            return [];
        }
    }, [setState, setPosts]);

    // Search posts by query, hashtag, or location
    const searchPosts = useCallback(async (params: { query?: string; hashtag?: string; addressid?: string }): Promise<string[]> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const posts = await socialAPI.searchPosts(params);
            
            if (posts && posts.length > 0) {
                // Process posts with interactions and distribute metrics
                const validPosts = posts.filter(validatePost);
                const postIds = validPosts.map(post => post.postid);

                await socialAPI.socialDatabase.savePosts(validPosts);
                
                // Update postsState with fetched posts
                setPosts(prev => ({
                    ...prev,
                    items: { ...prev.items, ...Object.fromEntries(validPosts.map(post => [post.postid, post])) }
                }));
                
                setState(prev => ({
                    ...prev,
                    searchResults: postIds,
                    loading: false
                }));
                
                return postIds;
            } else {
                setState(prev => ({ ...prev, searchResults: [], loading: false }));
                return [];
            }
        } catch (error) {
            setState(prev => ({ 
                ...prev, 
                error: error instanceof Error ? error.message : "Failed to search posts",
                loading: false
            }));
            console.error('Failed to search posts:', error);
            return [];
        }
    }, [setState, setPosts]);

    // Local search for posts by hashtag using cached data
    const searchLocalPostsByHashtag = useCallback(async (hashtag: string): Promise<string[]> => {
        try {
            // Search local post_hashtag table
            const postIds = await socialAPI.socialDatabase.getPostsByHashtag(hashtag);
            
            setState(prev => ({
                ...prev,
                searchResults: postIds
            }));
            
            return postIds;
        } catch (error) {
            console.error('Failed to search local posts by hashtag:', error);
            return [];
        }
    }, [setState]);

    // Add post to home feed for optimistic update
    const addPostToHomeFeed = useCallback((postId: string): void => {
        setState(prev => ({
            ...prev,
            homeFeed: [postId, ...prev.homeFeed]
        }));
    }, [setState]);

    // Load more posts for infinite scroll
    const loadMoreHomeFeed = useCallback(async (): Promise<PostType[]> => {
        const currentFeedLength = state.homeFeed.length;
        return await getHomeFeed({ limit: 10, offset: currentFeedLength, append: true });
    }, [state.homeFeed.length, getHomeFeed]);

    return {
        homeFeed: state.homeFeed,
        homeFeedGroups: state.homeFeedGroups,
        discoverFeed: state.discoverFeed,
        trendingFeed: state.trendingFeed,
        hashtagFeeds: state.hashtagFeeds,
        userFeeds: state.userFeeds,
        searchResults: state.searchResults,
        loading: state.loading,
        error: state.error,

        // Feed functions
        getHomeFeed,
        getDiscoverFeed,
        getTrendingFeed,
        searchPosts,
        searchLocalPostsByHashtag,
        addPostToHomeFeed,
        loadMoreHomeFeed,

        reset: resetFeeds
    };
};